import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

import ViewerVideo from '../../components/viewerVideo/ViewerVideo';
import ViewerImage from '../../components/viewerImage/ViewerImage';
import GoBackButton from '../../components/goBackButton/GoBackButton';
import Loader from '../../components/loader/Loader';
import ItemInfo from '../../components/itemInfo/ItemInfo';

import helpers from '../../js/helpers';
import { userStore, generalStore, useMiwiStore } from '../../store';
import Server from '../../js/config/_server';
import { apiGet, apiDelete } from '../../js/api';
import { REQUESTS, ROUTES, RESPONSE, OVERRIDE_SISSION_DURATION } from '../../js/_constants';
import { type DistributedMedium } from '../../api/types/index';
import { DeviceType } from '@org-avp/device-models';
import Config from '../../js/config/_global';
import IconButton from '../../components/iconButton/IconButton';
import Modal from '../../components/modal/Modal';
import PlaylistsOverlay from '../../components/playlistsOverlay/PlaylistsOverlay';
import Button from '../../components/button/Button';
import { useTranslation } from 'react-i18next';
import EditAssetModal from '../../components/editAssetModal/EditAssetModal';
import DeleteConfirmationModal from '../../components/deleteConfirmationModal/DeleteConfirmationModal';

import './itemView.scss';

const ItemView = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams<{ id: string }>();
    const { t } = useTranslation();
    const { setAlert, setPageName } = generalStore();
    const {
        overrideDeviceId,
        overrideMedia,
        overrideDeviceType,
        overrideSessionId,
        setAsset2D,
        setAsset3D,
        setOverride,
        controlMedia2D,
        setOverrideMediaUrl,
        setOverrideMedia,
    } = useMiwiStore();

    const { details } = userStore();
    
    const [loading, setLoading] = useState(true);

    const [item, setItem] = useState<DistributedMedium>({} as DistributedMedium);

    const [info, setInfo] = useState<DistributedMedium>();
    const [mediaType, setMediaType] = useState<string>('');
    const [selectedSource, setSelectedSource]= useState('my_playlists');
    const [isPlaylistModalOpen, setIsPlaylistModalOpen] = useState(false);
    const [isEditAssetOpen, setIsEditAssetOpen] = useState<boolean>(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isDeletingAsset, setIsDeletingAsset] = useState(false);
    const[sessionId, setSessionId] = useState<string>(overrideSessionId);

    useEffect(() => {
        setSessionId(overrideSessionId);
    }, [overrideSessionId]);

    const handleDeleteModalOpen = () => setIsDeleteModalOpen(true);

    const handleCloseDeleteModal = () => setIsDeleteModalOpen(false);

    const loadItem = mediaType => {
        setLoading(true);
        let pathId = `${Server.services.distribution.mediaItems}/${mediaType}/${params.id}`;
        if (
            typeof (location as any).query !== 'undefined' &&
            typeof (location as any).query.id !== 'undefined'
        ) {
            pathId = (location as any).query.id;
        }
        const url = `${Server.baseUrl}${pathId}`;
        apiGet<DistributedMedium>(url, { accept: REQUESTS.ACCEPT.JSON_LD })
            .then(json => {
                const { ...data } = json;
                setItem(json);
                setInfo(data);
                setPageName(json.title || '');
            })
            .then(() => setLoading(false));
    };

    useEffect(() => {
        const urlSplitPath = location.pathname.split('/');
        const mediaType = urlSplitPath[urlSplitPath.length - 2];
        setMediaType(mediaType);
        loadItem(mediaType);
    }, [params.id, location.pathname]);

    const handleCloseAssetEdit = () => {
        setIsEditAssetOpen(false);
    };

    const handleOpenAssetEdit = () => {
        setIsEditAssetOpen(true);
    };

    const specialViewComponent = () => {
        let specialComponent;
        switch (mediaType) {
            case 'videos':
                specialComponent = (
                    <ViewerVideo
                        name={item?.title || 'viewer-video'}
                        source={helpers.getMediaSource(item?.sourceFormats)}
                        aiCompliance={item?.aiCompliance}
                    />
                );
                break;
            case 'images':
                specialComponent = (
                    <ViewerImage
                        name={item?.title || 'viewer-image'}
                        source={item?.thumbnail}
                        aiCompliance={item?.aiCompliance}
                    />
                );
                break;
            default:
        }

        return specialComponent;
    };

    const connectToMiwi = async media => {
        const type = media['@type'].toLowerCase();
        media.type = type;
        const source = helpers.getMediaSource(media?.sourceFormats);
        const sessionTimeout = type === 'image' ? OVERRIDE_SISSION_DURATION : media.duration * 1000;
        if (overrideDeviceType === DeviceType.CHROMEBOX) {
            await setAsset2D(type, source, media)
            if(type === 'image'){
                Config.overrideSessionTimeout && clearTimeout(Config.overrideSessionTimeout);
                Config.overrideSessionTimeout = setTimeout(async() => {
                    controlMedia2D('SKIP');
                }, sessionTimeout);
            }
        } else if (overrideDeviceType === DeviceType.AVE)  {
            setAsset3D(type, source, media)
        } else {
            await setOverride(media, type, source);
            Config.overrideSessionTimeout && clearTimeout(Config.overrideSessionTimeout);
            Config.overrideSessionTimeout = setTimeout(async() => {
                setOverrideMediaUrl('');
                setOverrideMedia('');
            }, sessionTimeout);
        }
    }

    const openAddModal = () => {
        setIsPlaylistModalOpen(!isPlaylistModalOpen);
    };

    const deleteAsset = async (asset) => {
        try {
            setIsDeletingAsset(true);
            const url = Server.baseUrl + asset[RESPONSE.ID] + '/dealer-upload';
            await apiDelete(url)
            setAlert({
                message: t('asset_delete_success'),
                type: 'info',
                display: true
            });
            setIsDeletingAsset(false);
            navigate(ROUTES.MEDIA_LIBRARY);
        } catch(error) {
                console.error('Delete error:', error);
                setAlert({
                    message: t('asset_delete_failed'),
                    type: 'warning',
                    display: true
                });
                setIsDeletingAsset(false);
        }
    };

    return (
        <div className="b-item-view">
            <div className='back-button-wrapper'>
                <GoBackButton />
                {item && !loading && (<div className='item-actions'>
                {details.isAllowedToUpload && item.kvps.length > 0 && 
                <>
                    <button
                        onClick={handleDeleteModalOpen}
                        data-testid='delete-asset'
                    >
                        <IconButton icon="erase-l" />
                    </button>
                    <button
                        onClick={handleOpenAssetEdit}
                        data-testid='edit-asset'
                    >
                        <IconButton icon="edit" />
                    </button>
                </>}
                <IconButton
                    name='override-asset'
                    onClick={() => connectToMiwi(item)}
                    icon={
                        overrideMedia.uuid === item?.uuid
                            ? 'system-display-large-white'
                            : 'system-display-large'
                    }
                    disabled={!overrideDeviceId || helpers.getAssetLicenceStatus(item) === 'is-expired' || !helpers.getMediaSource(item?.sourceFormats)
                    }
                    color={overrideMedia.uuid === item?.uuid ? 'black' : ''}
                />
                {item && (item['@type'] as string) === 'Document' ? (
                    ''
                ) : (
                    <IconButton
                        name='add-asset-to-playlist'
                        onClick={openAddModal}
                        icon="system-add-large"
                        disabled={
                            helpers.getAssetLicenceStatus(item) === 'is-expired'
                        }
                    />
                )}
                </div>)}
            </div>
            {loading ? (
                <Loader loading={loading} />
            ) : (
                <div className="item-view">
                    {specialViewComponent()}
                    <ItemInfo info={info} data={item} />
                </div>
            )}
            <Modal
                show={isPlaylistModalOpen}
                onClose={openAddModal}
                className="overlay"
                position="left"
            >
                <Modal.Title>
                    <h4>{t('add_to_playlist')}</h4>
                </Modal.Title>
                <Modal.Content>
                    <div className="button-container">
                        <Button
                            clickHandler={() => setSelectedSource('my_playlists')}
                            text={t('my_playlists')}
                            secondaryButton={selectedSource !== 'my_playlists'}
                        />
                        <Button
                            clickHandler={() => setSelectedSource('market_playlists')}
                            text={t('market_playlists')}
                            secondaryButton={selectedSource !== 'market_playlists'}
                        />
                    </div>
                    {selectedSource === 'my_playlists' &&  <PlaylistsOverlay
                        selectedMediaItems={[item]}
                    />}
                    {selectedSource === 'market_playlists' && <PlaylistsOverlay
                        market
                        selectedMediaItems={[item]}
                    />}
                </Modal.Content>
            </Modal>
            {isEditAssetOpen && (
                <EditAssetModal
                    onClose={handleCloseAssetEdit}
                    asset={item}
                    succesfull={() => loadItem(mediaType)}
                />
            )}
            <DeleteConfirmationModal
                show={isDeleteModalOpen}
                onClose={handleCloseDeleteModal}
                onCancel={handleCloseDeleteModal}
                onAction={() => deleteAsset(item)}
                isLoading={isDeletingAsset}
            >
                <h4>{t('assset_delete_confirm')}</h4>
            </DeleteConfirmationModal>
        </div>
    );
};

export default ItemView;
